import {
  Box,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { ApexOptions } from "apexcharts";
import { useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  CardStats,
  DailyStats,
  MonthlyStats,
  WeeklyStats,
} from "../../types/analytics";
import { useAppTheme } from "../../context/ThemeContext";

type DataStats = WeeklyStats | MonthlyStats | DailyStats;
type Periods = "daily" | "monthly" | "weekly";
type Source = "students" | "submissions" | "transactions";

const SelectPeriod = ({
  period,
  handleSelectPeriod,
}: {
  period: Periods;
  handleSelectPeriod: any;
}) => (
  <Select
    size="small"
    sx={{ textTransform: "capitalize", minWidth: 100 }}
    value={period}
    onChange={handleSelectPeriod}>
    {["daily", "weekly", "monthly"].map((p) => (
      <MenuItem sx={{ textTransform: "capitalize" }} key={p} value={p}>
        {p}
      </MenuItem>
    ))}
  </Select>
);

const generateChartData = (data: DataStats[], name: string) => {
  if (!data) return { name, data: [], dates: [] };
  return data.reduce(
    (acc: any, curr) => {
      acc.data.push(curr.total);
      acc.dates.push(curr.date[0].formattedDate);
      return acc;
    },
    { name, data: [], dates: [] }
  );
};

const SOURCE_COLORS: Record<Source, string> = {
  students: "#059669",
  submissions: "#0EA5E9",
  transactions: "#0F172A",
};

const ALL_SOURCES: Source[] = ["students", "submissions", "transactions"];

// Max x-axis ticks to show per period so labels don't overlap
const TICK_AMOUNTS: Record<Periods, number> = {
  daily: 12,
  weekly: 8,
  monthly: 6,
};

function Chart({
  data,
  period,
  handleSelectPeriod,
}: {
  data: CardStats;
  period: Periods;
  handleSelectPeriod: any;
}) {
  const theme = useTheme();
  const { isNewTheme, accentColor: globalAccent } = useAppTheme();
  const [activeSources, setActiveSources] = useState<Source[]>(["students"]);
  const [chartType, setChartType] = useState<"bar" | "area">("bar");

  const colors = activeSources.map((s) =>
    isNewTheme && s === "students" ? globalAccent : SOURCE_COLORS[s]
  );

  const allDates = Array.from(
    new Set(
      activeSources.flatMap((s) =>
        (data[s][period]?.stats ?? []).map((d: DataStats) => d.date[0].formattedDate)
      )
    )
  ).sort();

  const series = activeSources.map((s) => {
    const raw = generateChartData(data[s][period]?.stats, s);
    const dateMap: Record<string, number> = {};
    raw.dates.forEach((d: string, i: number) => { dateMap[d] = raw.data[i]; });
    return { name: s.charAt(0).toUpperCase() + s.slice(1), data: allDates.map((d) => dateMap[d] ?? 0) };
  });

  const getFill = (): ApexFill => {
    if (chartType === "bar") return { opacity: 1 };
    return isNewTheme
      ? { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [0, 100] } }
      : { opacity: 0.3 };
  };

  const chartOptions: ApexOptions = {
    chart: {
      height: 350,
      type: chartType,
      toolbar: { show: false },
      fontFamily: theme.typography.fontFamily,
      background: "transparent",
      animations: { enabled: true, speed: 300 },
    },
    colors,
    fill: getFill(),
    plotOptions: { bar: { borderRadius: 4, columnWidth: activeSources.length > 1 ? "70%" : "55%" } },
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: chartType === "bar" ? 0 : isNewTheme ? 3 : 2,
    },
    grid: {
      borderColor: isNewTheme ? "#E2E8F0" : theme.palette.divider,
      strokeDashArray: isNewTheme ? 4 : 0,
      padding: isNewTheme ? { left: 8, right: 8 } : {},
    },
    xaxis: {
      type: "category",
      categories: allDates,
      tickAmount: TICK_AMOUNTS[period],
      axisBorder: { show: !isNewTheme },
      axisTicks: { show: !isNewTheme },
      labels: {
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        maxHeight: 72,
        style: { colors: isNewTheme ? "#64748B" : "#666", fontWeight: isNewTheme ? 500 : 400, fontSize: "10px" },
      },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        style: { colors: isNewTheme ? "#64748B" : "#666", fontWeight: isNewTheme ? 600 : 400, fontSize: "11px" },
      },
    },
    tooltip: { theme: "light", style: { fontFamily: theme.typography.fontFamily } },
    legend: { position: "top", show: activeSources.length > 1 },
    markers: isNewTheme
      ? { size: 4, strokeColors: "#fff", strokeWidth: 2, hover: { size: 6 } }
      : { size: 0 },
  };

  const handleToggleSource = (src: Source) => {
    setActiveSources((prev) =>
      prev.includes(src)
        ? prev.length > 1 ? prev.filter((s) => s !== src) : prev
        : [...prev, src]
    );
  };

  return (
    <Grid container direction="column" sx={{ height: "100%", width: "100%" }}>
      <Grid item container direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 1 }}>
        {isNewTheme ? (
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ letterSpacing: "0.02em" }}>
              Trend Overview
            </Typography>
            <Box sx={{ width: 32, height: 3, borderRadius: 999, backgroundColor: colors[0], mt: 0.5, transition: "background-color 0.2s ease" }} />
          </Box>
        ) : (
          <Box />
        )}

        <ButtonGroup sx={{ gap: 1, alignItems: "center" }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {ALL_SOURCES.map((src) => (
              <FormControlLabel
                key={src}
                control={
                  <Checkbox
                    size="small"
                    checked={activeSources.includes(src)}
                    onChange={() => handleToggleSource(src)}
                    sx={{ p: 0.5, color: SOURCE_COLORS[src], "&.Mui-checked": { color: SOURCE_COLORS[src] } }}
                  />
                }
                label={<Typography variant="caption" fontWeight={600} sx={{ textTransform: "capitalize" }}>{src}</Typography>}
                sx={{ mx: 0 }}
              />
            ))}
          </Stack>
          <ToggleButtonGroup
            size="small"
            value={chartType}
            exclusive
            onChange={(_, val) => val && setChartType(val)}
            sx={{ height: 40 }}>
            {(["bar", "area"] as const).map((t) => (
              <ToggleButton key={t} value={t} sx={{ px: 1.5, fontSize: "0.75rem", fontWeight: 600, textTransform: "none" }}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <SelectPeriod handleSelectPeriod={handleSelectPeriod} period={period} />
        </ButtonGroup>
      </Grid>

      <Grid flex={10} item>
        <ReactApexChart key={`${chartType}-${activeSources.join()}`} height="100%" type={chartType} options={chartOptions} series={series} />
      </Grid>
    </Grid>
  );
}

export default Chart;
